// src/pages/me/in-kind-order.js
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Client from "../../utils/client";
import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { api_url } from "../../constant";
import OrderInfo from "./components/orders/OrderInfo";

// Translation for in-kind statuses
export const translateInKindStatus = (status) => {
    switch (status) {
        case "Dispatched": return "تم تحميل الشحنة من المخزن";
        case "InTransit": return "الشحنة في الطريق";
        case "Delivered": return "وصلت لموقع التعاونية";
        case "Received": return "تم الاستلام والتأكيد من التعاونية";
        case "Cancelled": return "ملغي";
        default: return status;
    }
};

// Translation for regular statuses (for reference)
export const translateStatus = (status) => {
    switch (status) {
        case "Pending": return "قيد الانتظار";
        case "Processing": return "قيد المعالجة";
        case "Shipped": return "تم الشحن";
        case "Delivered": return "تم التوصيل";
        case "Paid": return "تم الدفع";
        case "Cancelled": return "إلغاء";
        default: return status;
    }
};

// Helper function to get images for a specific status
const getImagesForStatus = (order, status) => {
    if (!order) return [];
    
    const statusMap = {
        'Dispatched': order.dispatchedImages || [],
        'InTransit': order.inTransitImages || [],
        'Delivered': order.deliveredImages || [],
        'Received': order.receivedImages || [],
    };
    
    return statusMap[status] || [];
};

const InKindOrderDetails = ({ currentUser }) => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [selectedInKindStatus, setSelectedInKindStatus] = useState("");
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showStatusForm, setShowStatusForm] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [images, setImages] = useState([]);
    const [mediaPreviews, setMediaPreviews] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [notes, setNotes] = useState({
        dispatchNotes: "",
        deliveryNotes: "",
        receiptNotes: ""
    });
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedStatusImages, setSelectedStatusImages] = useState({
        status: "",
        images: [],
        notes: ""
    });
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const fileInputRef = useRef(null);

    const { data: order, isLoading, error } = useQuery({
        queryKey: ["inKindOrder", orderId],
        queryFn: async () => {
            const response = await Client.get(`/orders/${orderId}`);
            
            // Verify this is an in-kind order
            if (!response.data.isInKind) {
                toast.error("هذا الطلب ليس طلب مساهمة عينية");
                navigate(`/me/orders/${orderId}`);
                return null;
            }
            
            return response.data;
        },
        enabled: !!orderId,
    });

    // Function to open image modal for a specific status
    const openImageModal = (status) => {
        const statusImages = getImagesForStatus(order, status);
        const statusNotes = order[`${status.toLowerCase()}Notes`] || "";
        
        if (statusImages.length === 0) {
            toast.info(`لا توجد صور متاحة لحالة ${translateInKindStatus(status)}`);
            return;
        }
        
        setSelectedStatusImages({
            status,
            images: statusImages,
            notes: statusNotes
        });
        setCurrentImageIndex(0);
        setShowImageModal(true);
    };

    // Navigate through images
    const nextImage = () => {
        setCurrentImageIndex((prevIndex) => 
            prevIndex === selectedStatusImages.images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const prevImage = () => {
        setCurrentImageIndex((prevIndex) => 
            prevIndex === 0 ? selectedStatusImages.images.length - 1 : prevIndex - 1
        );
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!showImageModal) return;
            
            if (e.key === 'ArrowRight') {
                nextImage();
            } else if (e.key === 'ArrowLeft') {
                prevImage();
            } else if (e.key === 'Escape') {
                setShowImageModal(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showImageModal, selectedStatusImages.images.length]);

    if (isLoading) {
        return <div className="text-center mt-10 text-gray-600">جارٍ تحميل الطلب...</div>;
    }

    if (error) {
        return <div className="text-center mt-10 text-red-500">حدث خطأ أثناء جلب بيانات الطلب</div>;
    }

    if (!order) {
        return <div className="text-center mt-10 text-red-500">الطلب غير موجود.</div>;
    }

    const isSeller = localStorage.getItem("user_id") === order?.negotiation?.sellerId;
    const isBuyer = localStorage.getItem("user_id") === order?.negotiation?.buyerId;

    // In-kind status flow rules
    const inKindStatusFlow = {
        [null]: ["Dispatched", "Cancelled"], // Initial status
        Dispatched: ["InTransit", "Cancelled"],
        InTransit: ["Delivered", "Cancelled"],
        Delivered: ["Received", "Cancelled"],
        Received: [], // Final status
        Cancelled: [],
    };

    const getAllowedStatuses = () => {
        const currentStatus = order?.inKindStatus || null;
        const allowedStatuses = inKindStatusFlow[currentStatus] || [];
        
        // Filter based on user role
        return allowedStatuses.filter(status => {
            // Seller can update Dispatched and InTransit
            if (isSeller && ["Dispatched", "InTransit"].includes(status)) {
                return true;
            }
            // Both seller and buyer can update Delivered and Cancelled
            if ((isSeller || isBuyer) && ["Delivered", "Cancelled"].includes(status)) {
                return true;
            }
            // Buyer can update Received
            if (isBuyer && status === "Received") {
                return true;
            }
            return false;
        });
    };

    const allowedStatuses = getAllowedStatuses();

    const handleImageUpload = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                resolve({
                    fileObj: file,
                    preview: e.target.result
                });
            };

            reader.onerror = () => {
                reject(new Error(`Failed to process ${file.name}`));
            };

            reader.readAsDataURL(file);
        });
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        if (!e.target.files?.length) return;

        try {
            const files = Array.from(e.target.files);

            const validFiles = files.filter(file => {
                const maxSizeMB = 10;
                if (file.size > maxSizeMB * 1024 * 1024) {
                    toast.error(`حجم الملف ${file.name} تخطى ${maxSizeMB}MB`);
                    return false;
                }
                if (!file.type.startsWith('image/')) {
                    toast.error(`الملف ${file.name} ليس صورة`);
                    return false;
                }
                return true;
            });

            if (validFiles.length === 0) return;

            if (images.length + validFiles.length > 5) {
                toast.error("يمكنك رفع 5 صورة فقط كحد أقصى");
                return;
            }

            const results = await Promise.all(
                validFiles.map(file => handleImageUpload(file))
            );

            setMediaPreviews(prev => [...prev, ...results.map(r => r.preview)]);
            setImages(prev => [...prev, ...results.map(r => r.fileObj)]);

            e.target.value = '';
        } catch (error) {
            toast.error('حدثت مشكلة اثناء معالجة الملفات');
            console.error(error);
        }
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        setMediaPreviews(prev => prev.filter((_, i) => i !== index));
    };

    // Upload images for specific in-kind status
    const uploadInKindImages = async (status) => {
        try {
            if (images.length === 0) return null;

            const formData = new FormData();
            images.forEach((file) => {
                formData.append("images", file);
            });

            const response = await fetch(`${api_url}/files/upload-images/order/${order.id}?category=${status.toLowerCase()}`, {
                method: "POST",
                body: formData,
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "فشل في تحميل الصور");
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    };

    const updateInKindStatus = async (newStatus) => {
        setIsSubmitting(true);
        try {
            // Upload images based on status if needed
            if (images.length > 0) {
                setIsUploading(true);
                try {
                    await uploadInKindImages(newStatus);
                    setImages([]);
                    setMediaPreviews([]);
                    toast.success("تم تحميل الصور بنجاح");
                } finally {
                    setIsUploading(false);
                }
            }

            // Prepare update data
            const updateData = {
                inKindStatus: newStatus
            };

            // Add appropriate notes based on status
            if (newStatus === "Dispatched" && notes.dispatchNotes) {
                updateData.dispatchNotes = notes.dispatchNotes;
            }
            if (newStatus === "Delivered" && notes.deliveryNotes) {
                updateData.deliveryNotes = notes.deliveryNotes;
            }
            if (newStatus === "Received" && notes.receiptNotes) {
                updateData.receiptNotes = notes.receiptNotes;
            }
            if (newStatus === "Cancelled" && cancelReason) {
                updateData.reason = cancelReason;
            }

            // Update order status
            const response = await Client.patch(`/orders/${order.id}/status`, updateData);

            // Invalidate queries to refresh data
            queryClient.invalidateQueries(["inKindOrder", orderId]);
            queryClient.invalidateQueries(["order", orderId]);
            
            // Reset states
            setShowCancelModal(false);
            setShowStatusForm(false);
            setCancelReason("");
            setSelectedInKindStatus("");
            setNotes({ dispatchNotes: "", deliveryNotes: "", receiptNotes: "" });
            
            toast.success("تم تحديث حالة الطلب بنجاح");
            return response;

        } catch (error) {
            toast.error(error.response?.data?.message || error.message || "فشل في تحديث حالة الطلب");
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStatusChange = (e) => {
        setSelectedInKindStatus(e.target.value);
    };

    const handleStatusUpdate = async () => {
        if (selectedInKindStatus === "Cancelled") {
            setShowCancelModal(true);
        } else {
            await updateInKindStatus(selectedInKindStatus);
        }
    };

    const handleConfirmCancel = async () => {
        if (cancelReason.trim()) {
            await updateInKindStatus("Cancelled");
        }
    };

    // Get notes label based on selected status
    const getNotesLabel = () => {
        switch (selectedInKindStatus) {
            case "Dispatched": return "ملاحظات التحميل";
            case "Delivered": return "ملاحظات التسليم للتعاونية";
            case "Received": return "ملاحظات الاستلام";
            default: return "ملاحظات";
        }
    };

    // Get notes placeholder based on selected status
    const getNotesPlaceholder = () => {
        switch (selectedInKindStatus) {
            case "Dispatched": return "أدخل تفاصيل الحمولة والكمية...";
            case "Delivered": return "أدخل تفاصيل الوصول والتعاونية...";
            case "Received": return "أدخل تفاصيل الاستلام والتأكيد...";
            default: return "أدخل ملاحظات...";
        }
    };

    // Update notes based on selected status
    const handleNotesChange = (e) => {
        const value = e.target.value;
        switch (selectedInKindStatus) {
            case "Dispatched":
                setNotes(prev => ({ ...prev, dispatchNotes: value }));
                break;
            case "Delivered":
                setNotes(prev => ({ ...prev, deliveryNotes: value }));
                break;
            case "Received":
                setNotes(prev => ({ ...prev, receiptNotes: value }));
                break;
            default:
                break;
        }
    };

    // Get current notes value based on selected status
    const getCurrentNotesValue = () => {
        switch (selectedInKindStatus) {
            case "Dispatched": return notes.dispatchNotes;
            case "Delivered": return notes.deliveryNotes;
            case "Received": return notes.receiptNotes;
            default: return "";
        }
    };

    return (
        <div dir="rtl" className="max-w-4xl mx-auto px-6 py-10 bg-white rounded-xl shadow-md">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-primary">تفاصيل الطلب - مساهمة عينية</h1>
                    <div className="mt-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full inline-flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        <span className="text-sm font-medium">طلب مساهمة عينية</span>
                    </div>
                </div>
                <button
                    onClick={() => navigate(`/me/orders/${orderId}`)}
                    className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
                >
                    عرض كطلب عادي
                </button>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                {/* Order Information */}
                <OrderInfo
                    order={order}
                    setShowStatusForm={setShowStatusForm}
                    allowedStatuses={allowedStatuses}
                    isInKind={true}
                />

                {/* In-Kind Status Timeline */}
                <div className="mt-8 mb-6">
                    <h3 className="text-lg font-bold mb-4">مسار المساهمة العينية</h3>
                    <div className="relative">
                        {/* Timeline line */}
                        <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 transform -translate-y-1/2"></div>
                        
                        {/* Status steps */}
                        <div className="flex justify-between relative z-10">
                            {["Dispatched", "InTransit", "Delivered", "Received"].map((status, index) => {
                                const isCompleted = order?.inKindStatus && 
                                    (status === "Dispatched" || 
                                     (status === "InTransit" && ["InTransit", "Delivered", "Received"].includes(order.inKindStatus)) ||
                                     (status === "Delivered" && ["Delivered", "Received"].includes(order.inKindStatus)) ||
                                     (status === "Received" && order.inKindStatus === "Received"));
                                
                                const isCurrent = order?.inKindStatus === status;
                                const statusImages = getImagesForStatus(order, status);
                                const hasImages = statusImages.length > 0;
                                
                                return (
                                    <div key={status} className="flex flex-col items-center">
                                        <div 
                                            className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 relative cursor-pointer transition-all duration-300 ${
                                                isCompleted ? 'bg-green-500 text-white hover:bg-green-600' :
                                                isCurrent ? 'bg-blue-500 text-white animate-pulse hover:bg-blue-600' :
                                                'bg-gray-200 text-gray-500 hover:bg-gray-300'
                                            } ${hasImages ? 'ring-2 ring-offset-2 ring-blue-400' : ''}`}
                                            onClick={() => openImageModal(status)}
                                            title={hasImages ? `عرض صور ${translateInKindStatus(status)} (${statusImages.length} صورة)` : translateInKindStatus(status)}
                                        >
                                            {index + 1}
                                            
                                            {/* Image indicator */}
                                            {hasImages && (
                                                <div className="absolute -top-1 -right-1 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                        <span className={`text-sm font-medium ${
                                            isCompleted ? 'text-green-600' :
                                            isCurrent ? 'text-blue-600' :
                                            'text-gray-500'
                                        }`}>
                                            {translateInKindStatus(status)}
                                        </span>
                                        {isCurrent && (
                                            <div className="mt-1 text-xs text-blue-500 font-bold">
                                                (الحالة الحالية)
                                            </div>
                                        )}
                                        {hasImages && (
                                            <div className="mt-1 text-xs text-green-600">
                                                ({statusImages.length} صور)
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Current Status Display */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                        <div>
                            <h4 className="font-bold text-gray-700">الحالة الحالية:</h4>
                            <p className="text-lg mt-1">
                                {order?.inKindStatus 
                                    ? translateInKindStatus(order.inKindStatus)
                                    : "لم يتم بدء العملية بعد"
                                }
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-gray-500">
                                آخر تحديث: {new Date(order?.updatedAt).toLocaleDateString('ar-SA')}
                            </p>
                        </div>
                    </div>
                    
                    {/* Display notes if available */}
                    {order?.dispatchNotes && (
                        <div className="mt-3 p-3 bg-white rounded border">
                            <p className="text-sm font-semibold text-gray-600">ملاحظات التحميل:</p>
                            <p className="text-gray-700 mt-1">{order.dispatchNotes}</p>
                        </div>
                    )}
                    {order?.deliveryNotes && (
                        <div className="mt-3 p-3 bg-white rounded border">
                            <p className="text-sm font-semibold text-gray-600">ملاحظات التسليم:</p>
                            <p className="text-gray-700 mt-1">{order.deliveryNotes}</p>
                        </div>
                    )}
                    {order?.receiptNotes && (
                        <div className="mt-3 p-3 bg-white rounded border">
                            <p className="text-sm font-semibold text-gray-600">ملاحظات الاستلام:</p>
                            <p className="text-gray-700 mt-1">{order.receiptNotes}</p>
                        </div>
                    )}

                    {/* Image preview section */}
                    <div className="mt-4">
                        <h5 className="font-bold text-gray-700 mb-2">الصور المرفوعة:</h5>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {["Dispatched", "InTransit", "Delivered", "Received"].map((status) => {
                                const statusImages = getImagesForStatus(order, status);
                                if (statusImages.length === 0) return null;
                                
                                return (
                                    <div 
                                        key={status}
                                        className="bg-white p-3 rounded-lg border cursor-pointer hover:border-blue-400 transition-colors"
                                        onClick={() => openImageModal(status)}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-gray-700">
                                                {translateInKindStatus(status)}
                                            </span>
                                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                {statusImages.length}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            {statusImages.slice(0, 2).map((img, index) => (
                                                <img
                                                    key={index}
                                                    src={img.startsWith('http') ? img : `${api_url}/${img}`}
                                                    alt={`${translateInKindStatus(status)} ${index + 1}`}
                                                    className="w-full h-20 object-cover rounded"
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = 'https://via.placeholder.com/150?text=صورة';
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Update Status Button */}
                {allowedStatuses.length > 0 && !showStatusForm && (
                    <div className="mt-6">
                        <button
                            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium"
                            onClick={() => setShowStatusForm(true)}
                        >
                            تحديث حالة الطلب
                        </button>
                    </div>
                )}

                {/* Status Update Form */}
                {showStatusForm && (
                    <div className="mt-8 p-6 border rounded-lg bg-gray-50">
                        <h2 className="text-xl font-bold mb-4">تحديث حالة المساهمة العينية</h2>

                        {/* Status Selection */}
                        <div className="mb-6">
                            <label className="block font-bold mb-2">اختر الحالة الجديدة:</label>
                            <select
                                className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                value={selectedInKindStatus}
                                onChange={handleStatusChange}
                            >
                                <option value="">اختر الحالة الجديدة</option>
                                {allowedStatuses.map((status, index) => (
                                    <option key={index} value={status}>
                                        {translateInKindStatus(status)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Notes Field (for specific statuses) */}
                        {selectedInKindStatus && ["Dispatched", "Delivered", "Received"].includes(selectedInKindStatus) && (
                            <div className="mb-6">
                                <label className="block font-bold mb-2">
                                    {getNotesLabel()}:
                                </label>
                                <textarea
                                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    placeholder={getNotesPlaceholder()}
                                    value={getCurrentNotesValue()}
                                    onChange={handleNotesChange}
                                    rows="3"
                                />
                            </div>
                        )}

                        {/* Image Upload Section */}
                        {selectedInKindStatus && selectedInKindStatus !== "Cancelled" && (
                            <div className="mb-6">
                                <p className="font-bold mb-3">
                                    إرفاق صور {selectedInKindStatus === "Dispatched" ? "للتحميل" : 
                                               selectedInKindStatus === "Delivered" ? "للتسليم" : 
                                               "للإستلام"}
                                </p>
                                
                                <div
                                    className="w-full h-32 border-2 border-dashed border-gray-300 bg-white rounded-lg flex flex-col justify-center items-center text-gray-500 cursor-pointer hover:border-blue-400 transition-colors"
                                    onClick={triggerFileInput}
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        className="hidden"
                                        onChange={handleFileChange}
                                        accept="image/*"
                                        multiple
                                    />
                                    <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                                    </svg>
                                    <span className="text-blue-500 underline">تحميل صور</span>
                                    <p className="text-xs text-gray-400 mt-1">(اختياري)</p>
                                </div>

                                {mediaPreviews?.length > 0 && (
                                    <div className="mt-4">
                                        <p className="text-sm font-medium text-gray-600 mb-2">الصور المرفوعة:</p>
                                        <div className="flex flex-wrap gap-3">
                                            {mediaPreviews.map((src, index) => (
                                                <div key={index} className="relative group">
                                                    <img
                                                        src={src}
                                                        className="w-20 h-20 object-cover border border-gray-300 rounded-lg"
                                                        alt={`Preview ${index + 1}`}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                        onClick={() => removeImage(index)}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                <p className="text-xs text-gray-500 mt-2">
                                    يمكنك تحميل حتى 5 صور كحد أقصى (PNG, JPG, GIF)
                                </p>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-4">
                            <button
                                type="button"
                                className="px-5 py-2.5 bg-gray-300 rounded-lg hover:bg-gray-400 font-medium"
                                onClick={() => {
                                    setSelectedInKindStatus("");
                                    setShowStatusForm(false);
                                    setImages([]);
                                    setMediaPreviews([]);
                                    setNotes({ dispatchNotes: "", deliveryNotes: "", receiptNotes: "" });
                                }}
                                disabled={isSubmitting || isUploading}
                            >
                                إلغاء
                            </button>
                            <button
                                type="button"
                                className="px-5 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={!selectedInKindStatus || isSubmitting || isUploading}
                                onClick={handleStatusUpdate}
                            >
                                {isUploading ? "جاري تحميل الصور..." : 
                                 isSubmitting ? "جاري التحديث..." : 
                                 "تأكيد التحديث"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Cancel Confirmation Modal */}
                {showCancelModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4 text-red-600">تأكيد إلغاء المساهمة العينية</h2>
                            <p className="text-gray-600 mb-4">
                                هل أنت متأكد من إلغاء هذه المساهمة العينية؟ هذا الإجراء لا يمكن التراجع عنه.
                            </p>
                            <textarea
                                className="w-full p-3 border rounded-lg h-28 focus:outline-none focus:ring-2 focus:ring-red-400"
                                placeholder="اكتب سبب الإلغاء..."
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                required
                            ></textarea>
                            <div className="flex justify-end gap-4 mt-6">
                                <button
                                    className="px-5 py-2.5 bg-gray-300 rounded-lg hover:bg-gray-400 font-medium"
                                    onClick={() => {
                                        setShowCancelModal(false);
                                        setCancelReason("");
                                    }}
                                    disabled={isSubmitting}
                                >
                                    إلغاء
                                </button>
                                <button
                                    className="px-5 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium disabled:opacity-50"
                                    onClick={handleConfirmCancel}
                                    disabled={!cancelReason.trim() || isSubmitting}
                                >
                                    {isSubmitting ? "جاري الإلغاء..." : "تأكيد الإلغاء"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Image View Modal */}
                {showImageModal && selectedStatusImages.images.length > 0 && (
                    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100] p-4">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                            {/* Header */}
                            <div className="flex justify-between items-center p-6 border-b">
                                <div>
                                    <h2 className="text-2xl font-bold text-primary">
                                        {translateInKindStatus(selectedStatusImages.status)}
                                    </h2>
                                    <p className="text-gray-600 mt-1">
                                        صورة {currentImageIndex + 1} من {selectedStatusImages.images.length}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowImageModal(false)}
                                    className="text-gray-500 hover:text-gray-700 text-2xl"
                                >
                                    ×
                                </button>
                            </div>

                            {/* Notes */}
                            {selectedStatusImages.notes && (
                                <div className="px-6 py-4 bg-blue-50 border-b">
                                    <p className="text-gray-700">
                                        <span className="font-bold">ملاحظات:</span> {selectedStatusImages.notes}
                                    </p>
                                </div>
                            )}

                            {/* Main Image */}
                            <div className="flex-1 flex items-center justify-center p-6 relative">
                                <button
                                    onClick={prevImage}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg z-10"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                
                                <img
                                    src={selectedStatusImages.images[currentImageIndex].startsWith('http') 
                                        ? selectedStatusImages.images[currentImageIndex] 
                                        : `${api_url}/${selectedStatusImages.images[currentImageIndex]}`
                                    }
                                    alt={`${translateInKindStatus(selectedStatusImages.status)} ${currentImageIndex + 1}`}
                                    className="max-w-full max-h-[60vh] object-contain rounded-lg"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'https://via.placeholder.com/600x400?text=صورة+غير+متوفرة';
                                    }}
                                />
                                
                                <button
                                    onClick={nextImage}
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg z-10"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>

                            {/* Thumbnails */}
                            <div className="p-4 border-t bg-gray-50">
                                <div className="flex overflow-x-auto space-x-2 p-2">
                                    {selectedStatusImages.images.map((img, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentImageIndex(index)}
                                            className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                                                index === currentImageIndex 
                                                    ? 'border-blue-500 ring-2 ring-blue-300' 
                                                    : 'border-gray-300 hover:border-blue-400'
                                            }`}
                                        >
                                            <img
                                                src={img.startsWith('http') ? img : `${api_url}/${img}`}
                                                alt={`Thumbnail ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-between items-center p-4 border-t">
                                <div className="text-sm text-gray-600">
                                    استخدم مفاتيح الأسهم ← → للتنقل بين الصور
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = selectedStatusImages.images[currentImageIndex].startsWith('http') 
                                                ? selectedStatusImages.images[currentImageIndex] 
                                                : `${api_url}/${selectedStatusImages.images[currentImageIndex]}`;
                                            link.download = `${selectedStatusImages.status}_${currentImageIndex + 1}.jpg`;
                                            link.click();
                                        }}
                                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                                    >
                                        تحميل الصورة
                                    </button>
                                    <button
                                        onClick={() => window.open(
                                            selectedStatusImages.images[currentImageIndex].startsWith('http') 
                                                ? selectedStatusImages.images[currentImageIndex] 
                                                : `${api_url}/${selectedStatusImages.images[currentImageIndex]}`,
                                            '_blank'
                                        )}
                                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                                    >
                                        فتح في نافذة جديدة
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </motion.div>
        </div>
    );
};

export default InKindOrderDetails;